import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { ReactComponent as BlueArrow } from 'assets/image/selects/blue-arrow.svg';
import { ReactComponent as WhiteArrow } from 'assets/image/selects/white-arrow.svg';
import { ORGANISATION_GENERAL_TYPES } from '@joc/api-gateway';
import { ORGANISATION_TYPE_OPTIONS, formatOrganisationGeneralType } from 'core/constants';
import styles from './index.module.scss';

type OrganizationTypeSelectProps = {
	fieldName: string;
	fieldValue?: ORGANISATION_GENERAL_TYPES | string;
	placeholder?: string;
	setFieldValue: (field: string, value: ORGANISATION_GENERAL_TYPES | string) => void;
	setFieldTouched?: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
	id?: string;
	classList?: Array<string>;
	parentClassName?: string;
	isDisabled?: boolean;
};

const OrganizationTypeSelect: FC<OrganizationTypeSelectProps> = ({
	fieldName,
	fieldValue,
	placeholder = 'Select type',
	setFieldValue,
	setFieldTouched,
	id,
	classList,
	parentClassName,
	isDisabled,
}) => {
	const [isShowOptions, setIsShowOptions] = useState<boolean>(false);
	const selectRef = useRef<HTMLDivElement>(null);
	const options = ORGANISATION_TYPE_OPTIONS;

	const title = useMemo(() => {
		if (fieldValue) {
			return formatOrganisationGeneralType(fieldValue);
		}
		return '';
	}, [fieldValue]);

	const optionClickHandler = (value: ORGANISATION_GENERAL_TYPES): void => {
		setFieldValue(fieldName, value);
		setIsShowOptions(false);
	};

	const handleClickOutside = (event: Event) => {
		if (isShowOptions && setFieldTouched) setFieldTouched(fieldName, true);
		if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
			setIsShowOptions(false);
		}
	};

	const titleClickHandler = () => {
		if (isDisabled) return;
		if (isShowOptions && setFieldTouched) setFieldTouched(fieldName, true);
		setIsShowOptions(!isShowOptions);
	};

	useEffect(() => {
		document.addEventListener('click', handleClickOutside, true);
		return () => document.removeEventListener('click', handleClickOutside, true);
	}, [isShowOptions]);

	return (
		<div
			id={id}
			className={cx(styles.dropdown_select, styles.orgTypeSelect, parentClassName, {
				[styles.md]: classList?.includes('md'),
				[styles.dropdown_select__active]: isShowOptions,
				[styles.disabled]: isDisabled,
			})}
			ref={selectRef}
		>
			<div
				className={cx(styles.dropdown_select__title, {
					[styles.dropdown_select__title_selected]: !!fieldValue,
				})}
				onClick={titleClickHandler}
			>
				<span className={cx({ [styles.disabled_span]: isDisabled })}>{title || placeholder}</span>
				{!isDisabled && !isShowOptions ? (
					<BlueArrow className={styles.dropdown_select__title__vector} />
				) : (
					<WhiteArrow className={styles.dropdown_select__title__vector} />
				)}
			</div>
			<div className={styles.dropdown_select__options}>
				{options.map(option => (
					<div
						key={option.value}
						className={styles.dropdown_select__options__item}
						onClick={() => optionClickHandler(option.value)}
					>
						{option.label}
					</div>
				))}
			</div>
		</div>
	);
};

export default OrganizationTypeSelect;
